import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Checkbox, FormControlLabel, Paper, TextField, MenuItem } from '@mui/material';
import { Loader2, Plus, RefreshCcw, Save, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import {
  createNutritionistIngredient,
  getIngredientCreationMetadata,
  IngredientCreationMetadataResponse,
  IngredientDuplicateMatch,
  searchIngredientDuplicates,
  TaxonomyItemType,
} from '@/lib/apiService';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { formatDisplayLabel } from '@/utils/display-labels';

interface IngredientFormState {
  itemType: TaxonomyItemType;
  sourceId: string;
  sourceName: string;
  sourceCode: string;
  sourceFoodName: string;
  name: string;
  description: string;
  aliasesInput: string;
  groupId: string;
  departmentId: string;
  processingState: string;
  physicalState: string;
  dietaryClass: string;
  referenceWeight: string;
  referenceUnit: string;
  isBranded: boolean;
  yieldFactor: string;
  retentionFactor: string;
  defaultServingId: string;
  servingOverrides: ServingOverrideDraft[];
  glycemicIndex: string;
  glycemicLoad: string;
  nutritionValues: Record<string, string>;
  dietaryTagIds: string[];
  allergenTagIds: string[];
  intoleranceTagIds: string[];
}

interface ServingOverrideDraft {
  id: string;
  servingId: string;
  servingSize: string;
  grams: string;
  ml: string;
  isDefault: boolean;
}

const formatTaxonomyDisplayName = (name: string): string =>
  formatDisplayLabel(name.replace(/&/g, ' and '));

const INGREDIENT_TEXT_SIZE = '14px';
const INGREDIENT_LABEL_SIZE = '13px';
const INGREDIENT_HELPER_SIZE = '13px';

const INGREDIENT_FORM_SX = {
  '& .MuiInputBase-input': {
    fontSize: INGREDIENT_TEXT_SIZE,
    lineHeight: 1.5,
    paddingTop: '14px',
    paddingBottom: '14px',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 52,
    alignItems: 'center',
    borderRadius: '8px',
  },
  '& .MuiInputLabel-root': {
    fontSize: INGREDIENT_LABEL_SIZE,
    lineHeight: 1.4,
    color: '#475467',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(1)',
    backgroundColor: '#FFFFFF',
    paddingLeft: '6px',
    paddingRight: '6px',
    lineHeight: 1.2,
  },
  '& .MuiOutlinedInput-notchedOutline legend > span': {
    paddingLeft: '6px',
    paddingRight: '6px',
  },
  '& .MuiFormHelperText-root': {
    fontSize: INGREDIENT_HELPER_SIZE,
    lineHeight: 1.4,
    marginTop: '6px',
  },
  '& .MuiSelect-select': { fontSize: INGREDIENT_TEXT_SIZE },
  '& .MuiFormControlLabel-label': { fontSize: INGREDIENT_TEXT_SIZE },
} as const;

const formatIngredientSelectValue = (
  value: unknown,
  options: Array<{ value: string; label: string }>,
): string => {
  const normalized = String(value ?? '');
  const match = options.find((option) => option.value === normalized);
  return match ? match.label : formatDisplayLabel(normalized);
};

const INGREDIENT_SELECT_MENU_PROPS = {
  MenuProps: {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': { fontSize: INGREDIENT_TEXT_SIZE },
      },
    },
  },
} as const;

/** Keep outlined labels floated so they never overlap select display text. */
const INGREDIENT_SHRINK_LABEL_PROPS = {
  InputLabelProps: { shrink: true },
} as const;

const createServingOverrideDraft = (): ServingOverrideDraft => ({
  id: `serv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  servingId: '',
  servingSize: '1',
  grams: '',
  ml: '',
  isDefault: false,
});

const ITEM_TYPE_OPTIONS: Array<{ value: TaxonomyItemType; label: string }> = [
  { value: 'IN', label: 'Ingredients' },
  { value: 'PKG', label: 'Packaged Food Products' },
  { value: 'BEV', label: 'Beverages' },
  { value: 'SUP', label: 'Supplements' },
];

const MACRO_NUTRITION_FIELDS = [
  { key: 'calories', label: 'Calories', unit: 'kcal' },
  { key: 'protein', label: 'Protein', unit: 'g' },
  { key: 'protein_e', label: 'Protein %E', unit: '%' },
  { key: 'carbohydrate', label: 'Carbohydrate', unit: 'g' },
  { key: 'carbohydrate_e', label: 'Carbohydrate %E', unit: '%' },
  { key: 'net_carbohydrate', label: 'Net Carbohydrate', unit: 'g' },
  { key: 'dietary_fibre', label: 'Dietary Fibre', unit: 'g' },
  { key: 'soluble_fibre_g', label: 'Soluble Fibre', unit: 'g' },
  { key: 'insoluble_fibre_g', label: 'Insoluble Fibre', unit: 'g' },
  { key: 'nsp_g', label: 'NSP', unit: 'g' },
  { key: 'sugar', label: 'Sugar', unit: 'g' },
  { key: 'total_sugars_e', label: 'Total Sugars %E', unit: '%' },
  { key: 'added_sugars_g', label: 'Added Sugars', unit: 'g' },
  { key: 'added_sugars_e', label: 'Added Sugars %E', unit: '%' },
  { key: 'free_sugars_g', label: 'Free Sugars', unit: 'g' },
  { key: 'free_sugars_e', label: 'Free Sugars %E', unit: '%' },
  { key: 'glucose_dextrose_g', label: 'Glucose (Dextrose)', unit: 'g' },
  { key: 'galactose_g', label: 'Galactose', unit: 'g' },
  { key: 'fructose_g', label: 'Fructose', unit: 'g' },
  { key: 'sucrose_g', label: 'Sucrose', unit: 'g' },
  { key: 'maltose_g', label: 'Maltose', unit: 'g' },
  { key: 'lactose_g', label: 'Lactose', unit: 'g' },
  { key: 'starch_g', label: 'Starch', unit: 'g' },
  { key: 'total_fat', label: 'Total Fat', unit: 'g' },
  { key: 'total_fat_e', label: 'Total Fat %E', unit: '%' },
  { key: 'saturated_fat', label: 'Saturated Fat', unit: 'g' },
  { key: 'saturated_fat_e', label: 'Saturated Fat %E', unit: '%' },
  { key: 'trans_fat', label: 'Trans Fat', unit: 'g' },
  { key: 'trans_fat_e', label: 'Trans Fat %E', unit: '%' },
  { key: 'mufa_mg', label: 'MUFA', unit: 'mg' },
  { key: 'mufa_e', label: 'MUFA %E', unit: '%' },
  { key: 'pufa_mg', label: 'PUFA', unit: 'mg' },
  { key: 'pufa_e', label: 'PUFA %E', unit: '%' },
  { key: 'n3_pufa_mg', label: 'n3 PUFA', unit: 'mg' },
  { key: 'n3_pufa_e', label: 'n3 PUFA %E', unit: '%' },
  { key: 'n6_pufa_mg', label: 'n6 PUFA', unit: 'mg' },
  { key: 'n6_pufa_e', label: 'n6 PUFA %E', unit: '%' },
  { key: 'epa_mg', label: 'EPA', unit: 'mg' },
  { key: 'dha_mg', label: 'DHA', unit: 'mg' },
  { key: 'ala_mg', label: 'ALA', unit: 'mg' },
  { key: 'dpa_mg', label: 'DPA', unit: 'mg' },
  { key: 'cholesterol', label: 'Cholesterol', unit: 'mg' },
  { key: 'alcohol', label: 'Alcohol', unit: 'g' },
  { key: 'water', label: 'Water', unit: 'g' },
] as const;

const MICRO_NUTRITION_FIELDS = [
  { key: 'vitamin_a_iu', label: 'Vitamin A', unit: 'IU' },
  { key: 'vitamin_a', label: 'Vitamin A', unit: 'mcg' },
  { key: 'vitamin_d', label: 'Vitamin D', unit: 'mcg' },
  { key: 'vitamin_d2_ergocalciferol_mcg', label: 'Vitamin D2 (Ergocalciferol)', unit: 'mcg' },
  { key: 'vitamin_d3_cholecalciferol_mcg', label: 'Vitamin D3 (Cholecalciferol)', unit: 'mcg' },
  { key: 'vitamin_d_iu', label: 'Vitamin D', unit: 'IU' },
  { key: 'vitamin_e_alpha_tocopherol_mg', label: 'Vitamin E (Alpha-Tocopherol)', unit: 'mg' },
  { key: 'vitamin_k_mcg', label: 'Vitamin K', unit: 'mcg' },
  { key: 'vitamin_b1_thiamin_mg', label: 'Vitamin B1 (Thiamin)', unit: 'mg' },
  { key: 'vitamin_b2_riboflavin_mg', label: 'Vitamin B2 (Riboflavin)', unit: 'mg' },
  { key: 'vitamin_b3_niacin_mg', label: 'Vitamin B3 (Niacin)', unit: 'mg' },
  { key: 'vitamin_b5_pantothenic_acid_mg', label: 'Vitamin B5 (Pantothenic Acid)', unit: 'mg' },
  { key: 'vitamin_b6_mg', label: 'Vitamin B6', unit: 'mg' },
  { key: 'vitamin_b7_biotin_mcg', label: 'Vitamin B7 (Biotin)', unit: 'mcg' },
  { key: 'vitamin_b9_folate_mcg', label: 'Vitamin B9 (Folate)', unit: 'mcg' },
  { key: 'vitamin_b12_mcg', label: 'Vitamin B12', unit: 'mcg' },
  { key: 'vitamin_c', label: 'Vitamin C', unit: 'mg' },
  { key: 'choline_mg', label: 'Choline', unit: 'mg' },
  { key: 'calcium', label: 'Calcium', unit: 'mg' },
  { key: 'phosphorus_mg', label: 'Phosphorus', unit: 'mg' },
  { key: 'magnesium_mg', label: 'Magnesium', unit: 'mg' },
  { key: 'sodium', label: 'Sodium', unit: 'mg' },
  { key: 'potassium', label: 'Potassium', unit: 'mg' },
  { key: 'chloride_mg', label: 'Chloride', unit: 'mg' },
  { key: 'iron', label: 'Iron', unit: 'mg' },
  { key: 'zinc_mg', label: 'Zinc', unit: 'mg' },
  { key: 'iodine_mcg', label: 'Iodine', unit: 'mcg' },
  { key: 'selenium_mcg', label: 'Selenium', unit: 'mcg' },
  { key: 'copper_mg', label: 'Copper', unit: 'mg' },
  { key: 'manganese_mg', label: 'Manganese', unit: 'mg' },
  { key: 'chromium_mcg', label: 'Chromium', unit: 'mcg' },
  { key: 'molybdenum_mcg', label: 'Molybdenum', unit: 'mcg' },
  { key: 'fluoride_mcg', label: 'Fluoride', unit: 'mcg' },
  { key: 'caffeine_mg', label: 'Caffeine', unit: 'mg' },
  { key: 'polyphenols_mg', label: 'Polyphenols', unit: 'mg' },
  { key: 'flavonoids_mg', label: 'Flavonoids', unit: 'mg' },
  { key: 'tannin_mg', label: 'Tannin', unit: 'mg' },
  { key: 'anthocyanins_mg', label: 'Anthocyanins', unit: 'mg' },
  { key: 'phytosterols_mg', label: 'Phytosterols', unit: 'mg' },
  { key: 'lycopene_mcg', label: 'Lycopene', unit: 'mcg' },
  { key: 'lutein_zeaxanthin_mcg', label: 'Lutein + Zeaxanthin', unit: 'mcg' },
  { key: 'beta_glucan_mg', label: 'Beta-Glucan', unit: 'mg' },
  { key: 'folic_acid_mcg', label: 'Folic Acid', unit: 'mcg' },
  { key: 'food_folate_mcg', label: 'Food Folate', unit: 'mcg' },
  { key: 'folate_dfe_mcg', label: 'Folate DFE', unit: 'mcg' },
  { key: 'betaine_mg', label: 'Betaine', unit: 'mg' },
  { key: 'retinol_mcg', label: 'Retinol', unit: 'mcg' },
  { key: 'carotene_beta_mcg', label: 'Carotene Beta', unit: 'mcg' },
  { key: 'carotene_alpha_mcg', label: 'Carotene Alpha', unit: 'mcg' },
  { key: 'dihydrophylloquinone_mcg', label: 'Dihydrophylloquinone', unit: 'mcg' },
  { key: 'menaquinone_4_mcg', label: 'Menaquinone-4', unit: 'mcg' },
  { key: 'tryptophan_mg', label: 'Tryptophan', unit: 'mg' },
  { key: 'threonine_mg', label: 'Threonine', unit: 'mg' },
  { key: 'isoleucine_mg', label: 'Isoleucine', unit: 'mg' },
  { key: 'leucine_mg', label: 'Leucine', unit: 'mg' },
  { key: 'lysine_mg', label: 'Lysine', unit: 'mg' },
  { key: 'methionine_mg', label: 'Methionine', unit: 'mg' },
  { key: 'cystine_mg', label: 'Cystine', unit: 'mg' },
  { key: 'phenylalanine_mg', label: 'Phenylalanine', unit: 'mg' },
  { key: 'tyrosine_mg', label: 'Tyrosine', unit: 'mg' },
  { key: 'valine_mg', label: 'Valine', unit: 'mg' },
  { key: 'arginine_mg', label: 'Arginine', unit: 'mg' },
  { key: 'histidine_mg', label: 'Histidine', unit: 'mg' },
  { key: 'alanine_mg', label: 'Alanine', unit: 'mg' },
  { key: 'aspartic_acid_mg', label: 'Aspartic Acid', unit: 'mg' },
  { key: 'glutamic_acid_mg', label: 'Glutamic Acid', unit: 'mg' },
  { key: 'glycine_mg', label: 'Glycine', unit: 'mg' },
  { key: 'proline_mg', label: 'Proline', unit: 'mg' },
  { key: 'serine_mg', label: 'Serine', unit: 'mg' },
  { key: 'hydroxyproline_mg', label: 'Hydroxyproline', unit: 'mg' },
  { key: 'theobromine_mg', label: 'Theobromine', unit: 'mg' },
  { key: 'pral_score', label: 'PRAL Score', unit: 'score' },
] as const;

const ALL_NUTRITION_FIELDS = [...MACRO_NUTRITION_FIELDS, ...MICRO_NUTRITION_FIELDS] as const;
type NutritionFieldKey = (typeof ALL_NUTRITION_FIELDS)[number]['key'];

const DEFAULT_NUTRITION_VALUES: Record<NutritionFieldKey, string> = ALL_NUTRITION_FIELDS.reduce(
  (acc, field) => {
    acc[field.key] = '';
    return acc;
  },
  {} as Record<NutritionFieldKey, string>,
);

const ACCENT = '#2EB4E4';
const GL_DISPLAY_DECIMALS = 2;

const formatDecimal = (value: number, fractionDigits = GL_DISPLAY_DECIMALS): string => {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(fractionDigits).replace(/\.?0+$/, '');
};

const DEFAULT_FORM_STATE: IngredientFormState = {
  itemType: 'IN',
  sourceId: '',
  sourceName: '',
  sourceCode: '',
  sourceFoodName: '',
  name: '',
  description: '',
  aliasesInput: '',
  groupId: '',
  departmentId: '',
  processingState: '',
  physicalState: '',
  dietaryClass: '',
  referenceWeight: '100',
  referenceUnit: 'gram',
  isBranded: false,
  yieldFactor: '',
  retentionFactor: '',
  defaultServingId: '',
  servingOverrides: [],
  glycemicIndex: '',
  glycemicLoad: '',
  nutritionValues: { ...DEFAULT_NUTRITION_VALUES },
  dietaryTagIds: [],
  allergenTagIds: [],
  intoleranceTagIds: [],
};

const IngredientsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const suggestedName = searchParams.get('name')?.trim() ?? '';
  const [metadata, setMetadata] = useState<IngredientCreationMetadataResponse['data'] | null>(null);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<IngredientFormState>(() => ({
    ...DEFAULT_FORM_STATE,
    name: suggestedName,
    nutritionValues: { ...DEFAULT_FORM_STATE.nutritionValues },
  }));
  const [duplicateQuery, setDuplicateQuery] = useState(suggestedName);
  const [duplicateMatches, setDuplicateMatches] = useState<IngredientDuplicateMatch[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateSearchMessage, setDuplicateSearchMessage] = useState('');

  const selectedGroup = useMemo(
    () => metadata?.groups.find((group) => group.id === form.groupId) || null,
    [metadata?.groups, form.groupId],
  );

  const selectedDepartmentOptions = useMemo(() => {
    return selectedGroup?.departments || [];
  }, [selectedGroup]);

  const servingUnitOptions = useMemo(() => {
    const units = new Set<string>();
    const referenceUnit = form.referenceUnit.trim().toLowerCase();
    if (referenceUnit) {
      units.add(referenceUnit);
    }
    for (const row of form.servingOverrides) {
      const unit = row.servingId.trim().toLowerCase();
      if (unit) {
        units.add(unit);
      }
    }
    return Array.from(units);
  }, [form.referenceUnit, form.servingOverrides]);

  const resolvedDefaultServingUnit = useMemo(
    () => form.defaultServingId.trim().toLowerCase() || form.referenceUnit.trim().toLowerCase(),
    [form.defaultServingId, form.referenceUnit],
  );

  const resolvedDefaultServingGrams = useMemo(() => {
    const referenceUnit = form.referenceUnit.trim().toLowerCase();
    const referenceWeightValue = Number(form.referenceWeight);

    if (!resolvedDefaultServingUnit) return null;

    if (resolvedDefaultServingUnit === referenceUnit) {
      return Number.isFinite(referenceWeightValue) && referenceWeightValue > 0
        ? referenceWeightValue
        : null;
    }

    const matchingOverride = form.servingOverrides.find(
      (row) => row.servingId.trim().toLowerCase() === resolvedDefaultServingUnit,
    );
    if (!matchingOverride) return null;

    const gramsValue = Number(matchingOverride.grams);
    return Number.isFinite(gramsValue) && gramsValue > 0 ? gramsValue : null;
  }, [form.referenceUnit, form.referenceWeight, form.servingOverrides, resolvedDefaultServingUnit]);

  const carbohydratePer100g = useMemo(() => {
    const value = Number(form.nutritionValues.carbohydrate || '');
    return Number.isFinite(value) && value >= 0 ? value : null;
  }, [form.nutritionValues.carbohydrate]);

  const carbsPerServing = useMemo(() => {
    if (carbohydratePer100g === null || resolvedDefaultServingGrams === null) return null;
    return (carbohydratePer100g * resolvedDefaultServingGrams) / 100;
  }, [carbohydratePer100g, resolvedDefaultServingGrams]);

  const glycemicIndexNumeric = useMemo(() => {
    const value = Number(form.glycemicIndex || '');
    return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
  }, [form.glycemicIndex]);

  const calculatedGlycemicLoad = useMemo(() => {
    if (glycemicIndexNumeric === null || carbsPerServing === null) return null;
    return (glycemicIndexNumeric * carbsPerServing) / 100;
  }, [glycemicIndexNumeric, carbsPerServing]);

  const glycemicLoadHelperText = useMemo(() => {
    const baseFormula = 'GL = (GI × carbs per serving) / 100';

    if (glycemicIndexNumeric === null) {
      return `${baseFormula}. Enter glycemic index to auto-calculate.`;
    }
    if (carbohydratePer100g === null) {
      return `${baseFormula}. Enter carbohydrate (per 100g) to auto-calculate.`;
    }
    if (resolvedDefaultServingGrams === null) {
      return `${baseFormula}. Provide valid default serving grams to auto-calculate.`;
    }

    return `${baseFormula}. Carbs/serving: ${formatDecimal(carbsPerServing || 0)} g.`;
  }, [carbohydratePer100g, carbsPerServing, glycemicIndexNumeric, resolvedDefaultServingGrams]);

  const itemTypeOptions = useMemo(
    () => ITEM_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  const processingStateOptions = useMemo(
    () => (metadata?.enums.processingStates || []).map((state) => ({
      value: state,
      label: formatDisplayLabel(state),
    })),
    [metadata?.enums.processingStates],
  );

  const physicalStateOptions = useMemo(
    () => (metadata?.enums.physicalStates || []).map((state) => ({
      value: state,
      label: formatDisplayLabel(state),
    })),
    [metadata?.enums.physicalStates],
  );

  const dietaryClassOptions = useMemo(
    () => (metadata?.enums.dietaryClasses || []).map((dietaryClass) => ({
      value: dietaryClass,
      label: formatDisplayLabel(dietaryClass),
    })),
    [metadata?.enums.dietaryClasses],
  );

  const groupOptions = useMemo(
    () => (metadata?.groups || []).map((group) => ({
      value: group.id,
      label: `${group.id} - ${formatTaxonomyDisplayName(group.name)}`,
    })),
    [metadata?.groups],
  );

  const departmentOptions = useMemo(
    () => selectedDepartmentOptions.map((department) => ({
      value: department.id,
      label: `${department.id} - ${formatTaxonomyDisplayName(department.name)}`,
    })),
    [selectedDepartmentOptions],
  );

  const dietaryTagMap = useMemo(() => (
    new Map((metadata?.tags.dietary || []).map((tag) => [tag.id, tag.displayName]))
  ), [metadata?.tags.dietary]);

  const allergenTagMap = useMemo(() => (
    new Map((metadata?.tags.allergen || []).map((tag) => [tag.id, tag.displayName]))
  ), [metadata?.tags.allergen]);

  const intoleranceTagMap = useMemo(() => (
    new Map((metadata?.tags.intolerance || []).map((tag) => [tag.id, tag.displayName]))
  ), [metadata?.tags.intolerance]);

  const applyMetadataToForm = useCallback(
    (nextMetadata: IngredientCreationMetadataResponse['data']) => {
      const fallbackGroup = nextMetadata.groups[0] || null;

      setForm((prev) => ({
        ...DEFAULT_FORM_STATE,
        itemType: nextMetadata.itemType,
        sourceId: nextMetadata.sourceId,
        sourceName: nextMetadata.sourceName,
        groupId: (() => {
          if (prev.groupId && nextMetadata.groups.some((group) => group.id === prev.groupId)) {
            return prev.groupId;
          }
          return fallbackGroup?.id || '';
        })(),
        departmentId: (() => {
          const resolvedGroupId = prev.groupId && nextMetadata.groups.some((group) => group.id === prev.groupId)
            ? prev.groupId
            : fallbackGroup?.id;
          const resolvedGroup = nextMetadata.groups.find((group) => group.id === resolvedGroupId);
          if (prev.departmentId && resolvedGroup?.departments.some((department) => department.id === prev.departmentId)) {
            return prev.departmentId;
          }
          return resolvedGroup?.departments[0]?.id || '';
        })(),
        processingState: nextMetadata.enums.processingStates.includes(prev.processingState)
          ? prev.processingState
          : nextMetadata.enums.processingStates[0] || '',
        physicalState: nextMetadata.enums.physicalStates.includes(prev.physicalState)
          ? prev.physicalState
          : nextMetadata.enums.physicalStates[0] || '',
        dietaryClass: nextMetadata.enums.dietaryClasses.includes(prev.dietaryClass)
          ? prev.dietaryClass
          : nextMetadata.enums.dietaryClasses[0] || '',
      }));
    },
    [],
  );

  const loadMetadata = useCallback(async (itemType: TaxonomyItemType = form.itemType) => {
    setLoadingMetadata(true);
    try {
      const response = await getIngredientCreationMetadata(itemType);
      setMetadata(response.data);
      applyMetadataToForm(response.data);
    } catch (error) {
      showErrorToast(error, 'Failed to load ingredient form metadata');
    } finally {
      setLoadingMetadata(false);
    }
  }, [applyMetadataToForm, form.itemType]);

  useEffect(() => {
    void loadMetadata();
  }, [loadMetadata]);

  const runDuplicateSearch = useCallback(async (rawQuery: string, silent = false) => {
    const query = rawQuery.trim();
    if (query.length < 2) {
      setDuplicateMatches([]);
      setDuplicateSearchMessage(query ? 'Enter at least 2 characters.' : '');
      return [] as IngredientDuplicateMatch[];
    }

    setCheckingDuplicates(true);
    if (!silent) setDuplicateSearchMessage('');
    try {
      const response = await searchIngredientDuplicates(query, 20);
      setDuplicateMatches(response.data.matches);
      setDuplicateSearchMessage(
        response.data.matches.length === 0
          ? 'No matching workbook or website ingredients found.'
          : `${response.data.matches.length} possible match${response.data.matches.length === 1 ? '' : 'es'} found.`,
      );
      return response.data.matches;
    } catch (error) {
      setDuplicateMatches([]);
      setDuplicateSearchMessage('Could not check existing ingredients.');
      if (!silent) showErrorToast(error, 'Duplicate search failed');
      return [] as IngredientDuplicateMatch[];
    } finally {
      setCheckingDuplicates(false);
    }
  }, []);

  useEffect(() => {
    const name = form.name.trim();
    if (name.length < 2) return undefined;
    const timeoutId = window.setTimeout(() => {
      setDuplicateQuery(name);
      void runDuplicateSearch(name, true);
    }, 450);
    return () => window.clearTimeout(timeoutId);
  }, [form.name, runDuplicateSearch]);

  const updateForm = <K extends keyof IngredientFormState>(key: K, value: IngredientFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateNutritionField = (key: NutritionFieldKey, value: string) => {
    setForm((prev) => ({
      ...prev,
      nutritionValues: {
        ...prev.nutritionValues,
        [key]: value,
      },
    }));
  };

  const addServingOverride = () => {
    setForm((prev) => ({
      ...prev,
      servingOverrides: [...prev.servingOverrides, createServingOverrideDraft()],
    }));
  };

  const updateServingOverride = (id: string, key: 'servingId' | 'servingSize' | 'grams' | 'ml', value: string) => {
    setForm((prev) => ({
      ...prev,
      servingOverrides: prev.servingOverrides.map((row) => (
        row.id === id ? { ...row, [key]: value } : row
      )),
    }));
  };

  const updateServingOverrideBoolean = (id: string, key: 'isDefault', value: boolean) => {
    setForm((prev) => ({
      ...prev,
      servingOverrides: prev.servingOverrides.map((row) => (
        row.id === id ? { ...row, [key]: value } : { ...row, isDefault: key === 'isDefault' && value ? false : row.isDefault }
      )),
      defaultServingId: value
        ? (prev.servingOverrides.find((row) => row.id === id)?.servingId || '').trim().toLowerCase()
        : prev.defaultServingId,
    }));
  };

  const removeServingOverride = (id: string) => {
    setForm((prev) => {
      const nextOverrides = prev.servingOverrides.filter((row) => row.id !== id);
      const selectedDefault = prev.defaultServingId.trim().toLowerCase();
      const stillValidDefault = selectedDefault
        && (
          (prev.referenceUnit.trim().toLowerCase() === selectedDefault)
          || nextOverrides.some((row) => row.servingId.trim().toLowerCase() === selectedDefault)
        );

      return {
        ...prev,
        servingOverrides: nextOverrides,
        defaultServingId: stillValidDefault ? prev.defaultServingId : '',
      };
    });
  };

  const updateTagSelection = (
    key: 'dietaryTagIds' | 'allergenTagIds' | 'intoleranceTagIds',
    value: unknown,
  ) => {
    const rawValues = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',')
        : [];
    const nextValues = rawValues
      .map((item) => String(item).trim())
      .filter(Boolean);
    updateForm(key, Array.from(new Set(nextValues)));
  };

  const renderSelectedTags = (selected: unknown, tagMap: Map<string, string>): string => {
    const selectedValues = Array.isArray(selected)
      ? selected
      : typeof selected === 'string'
        ? selected.split(',')
        : [];
    if (selectedValues.length === 0) return '';
    return selectedValues
      .map((value) => {
        const id = String(value);
        return formatDisplayLabel(tagMap.get(id) || id);
      })
      .join(', ');
  };

  const parseNutritionFields = (): Record<string, number> => {
    const nutrition: Record<string, number> = {};

    for (const field of ALL_NUTRITION_FIELDS) {
      const rawValue = (form.nutritionValues[field.key] || '').trim();
      if (!rawValue) continue;

      const parsedValue = Number(rawValue);
      if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        throw new Error(`${field.label} must be a non-negative number`);
      }
      nutrition[field.key] = parsedValue;
    }

    return nutrition;
  };

  const parseServingOverrides = (): Array<{
    servingId: string;
    servingSize?: number | null;
    grams: number;
    ml?: number | null;
    isDefault?: boolean;
  }> => {
    const parsedRows: Array<{
      servingId: string;
      servingSize?: number | null;
      grams: number;
      ml?: number | null;
      isDefault?: boolean;
    }> = [];

    for (let index = 0; index < form.servingOverrides.length; index += 1) {
      const row = form.servingOverrides[index];
      const unit = row.servingId.trim().toLowerCase();
      const servingSizeRaw = row.servingSize.trim();
      const gramsRaw = row.grams.trim();
      const mlRaw = row.ml.trim();

      if (!unit && !servingSizeRaw && !gramsRaw && !mlRaw) continue;
      if (!unit || !gramsRaw) {
        throw new Error(`Serving row ${index + 1}: unit and grams are required`);
      }

      const servingSizeValue = servingSizeRaw ? Number(servingSizeRaw) : null;
      if (servingSizeValue !== null && (!Number.isFinite(servingSizeValue) || servingSizeValue <= 0)) {
        throw new Error(`Serving row ${index + 1}: serving size must be a positive number`);
      }

      const gramsValue = Number(gramsRaw);
      if (!Number.isFinite(gramsValue) || gramsValue <= 0) {
        throw new Error(`Serving row ${index + 1}: grams must be a positive number`);
      }

      const mlValue = mlRaw ? Number(mlRaw) : null;
      if (mlValue !== null && (!Number.isFinite(mlValue) || mlValue <= 0)) {
        throw new Error(`Serving row ${index + 1}: ml must be a positive number`);
      }

      parsedRows.push({
        servingId: unit,
        servingSize: servingSizeValue,
        grams: gramsValue,
        ml: mlValue,
        isDefault: row.isDefault,
      });
    }

    return parsedRows;
  };

  const handleGroupChange = (groupId: string) => {
    const group = metadata?.groups.find((item) => item.id === groupId);
    updateForm('groupId', groupId);
    updateForm('departmentId', group?.departments[0]?.id || '');
  };

  const handleItemTypeChange = (itemType: TaxonomyItemType) => {
    setForm((prev) => ({ ...prev, itemType }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showErrorToast(new Error('Ingredient name is required'), 'Validation');
      return;
    }

    if (!form.groupId) {
      showErrorToast(new Error('Group is required'), 'Validation');
      return;
    }

    if (!form.departmentId) {
      showErrorToast(new Error('Department is required'), 'Validation');
      return;
    }

    if (!form.processingState) {
      showErrorToast(new Error('Processing state is required'), 'Validation');
      return;
    }

    if (!form.physicalState) {
      showErrorToast(new Error('Physical state is required'), 'Validation');
      return;
    }

    if (!form.dietaryClass) {
      showErrorToast(new Error('Dietary class is required'), 'Validation');
      return;
    }

    let nutritionPer100g: Record<string, number> = {};
    try {
      nutritionPer100g = parseNutritionFields();
    } catch (error) {
      showErrorToast(error, 'Nutrition Error');
      return;
    }

    let servingOverrides: Array<{
      servingId: string;
      servingSize?: number | null;
      grams: number;
      ml?: number | null;
      isDefault?: boolean;
    }> = [];
    try {
      servingOverrides = parseServingOverrides();
    } catch (error) {
      showErrorToast(error, 'Serving Error');
      return;
    }

    const aliases = form.aliasesInput
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const referenceWeightValue = Number(form.referenceWeight);
    if (!form.referenceWeight.trim() || !Number.isFinite(referenceWeightValue) || referenceWeightValue <= 0) {
      showErrorToast(new Error('Reference weight must be a positive number'), 'Validation');
      return;
    }

    const referenceUnitValue = form.referenceUnit.trim().toLowerCase();
    if (!referenceUnitValue) {
      showErrorToast(new Error('Reference unit is required'), 'Validation');
      return;
    }

    const yieldFactorValue = form.yieldFactor.trim() ? Number(form.yieldFactor) : null;
    if (yieldFactorValue !== null && (!Number.isFinite(yieldFactorValue) || yieldFactorValue <= 0)) {
      showErrorToast(new Error('Yield factor must be a positive number'), 'Validation');
      return;
    }

    const retentionFactorValue = form.retentionFactor.trim() ? Number(form.retentionFactor) : null;
    if (retentionFactorValue !== null && (!Number.isFinite(retentionFactorValue) || retentionFactorValue <= 0)) {
      showErrorToast(new Error('Retention factor must be a positive number'), 'Validation');
      return;
    }

    const glycemicIndexValue = form.glycemicIndex.trim()
      ? Number(form.glycemicIndex)
      : null;
    if (glycemicIndexValue !== null && (!Number.isFinite(glycemicIndexValue) || glycemicIndexValue < 0 || glycemicIndexValue > 100)) {
      showErrorToast(new Error('Glycemic index must be between 0 and 100'), 'Validation');
      return;
    }

    const glycemicLoadValue = calculatedGlycemicLoad;
    if (glycemicLoadValue !== null && (!Number.isFinite(glycemicLoadValue) || glycemicLoadValue < 0)) {
      showErrorToast(new Error('Glycemic load must be a non-negative number'), 'Validation');
      return;
    }

    const duplicateTerms = Array.from(new Set([
      form.name.trim(),
      form.sourceCode.trim(),
      ...aliases,
    ].filter((value) => value.length >= 2)));
    for (const term of duplicateTerms) {
      const matches = await runDuplicateSearch(term, true);
      const exactMatch = matches.find((match) => match.exactMatch);
      if (exactMatch) {
        setDuplicateQuery(term);
        showErrorToast(
          new Error(
            `"${term}" already matches ${exactMatch.name} (${exactMatch.sourceId || 'no code'}) ` +
            `from ${exactMatch.sourceLabel.toLowerCase()}. Review the match before creating a new ingredient.`,
          ),
          'Possible duplicate ingredient',
        );
        return;
      }
    }

    setSaving(true);
    try {
      const response = await createNutritionistIngredient({
        itemType: form.itemType,
        sourceCode: form.sourceCode.trim() || null,
        sourceFoodName: form.sourceFoodName.trim() || null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        aliases,
        groupId: form.groupId,
        departmentId: form.departmentId,
        processingState: form.processingState,
        physicalState: form.physicalState,
        dietaryClass: form.dietaryClass,
        dietaryTagIds: form.dietaryTagIds,
        allergenTagIds: form.allergenTagIds,
        intoleranceTagIds: form.intoleranceTagIds,
        nutritionPer100g,
        servingOverrides,
        defaultServingId: form.defaultServingId.trim() || null,
        glycemicIndex: glycemicIndexValue,
        glycemicLoad: glycemicLoadValue,
        referenceWeight: referenceWeightValue,
        referenceUnit: referenceUnitValue,
        isBranded: form.isBranded,
        yieldFactor: yieldFactorValue,
        retentionFactor: retentionFactorValue,
      });

      showSuccessToast('Ingredient Created', `${response.data.name} (${response.data.sourceId}) created successfully.`);
      await loadMetadata();
    } catch (error) {
      showErrorToast(error, 'Failed to create ingredient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          border: '1px solid #BDE7F5',
          bgcolor: '#FFFFFF',
        }}
      >
        <Box className="space-y-6 bg-white px-4 pb-6 pt-6 sm:px-5 lg:px-8" sx={INGREDIENT_FORM_SX}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-[20px] font-semibold leading-snug text-slate-900">Ingredients</h1>
              <p className="mt-1 text-[14px] text-slate-500">
                Add internal food items with taxonomy group, department, nutrition, serving conversions, and safety tags.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <Button
                onClick={() => void loadMetadata()}
                disabled={loadingMetadata || saving}
                className="h-8 gap-1.5 rounded-md border-0 px-2.5 text-[13px] font-semibold text-white shadow-none hover:brightness-95"
                style={{ backgroundColor: ACCENT }}
              >
                <RefreshCcw className="h-3 w-3" />
                Refresh Preview
              </Button>
              <NotificationCenter buttonVariant="feature" />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-cyan-200 bg-cyan-50/40 p-4">
            <div>
              <h2 className="text-[16px] font-semibold text-slate-900">Check existing ingredients</h2>
              <p className="text-[14px] text-slate-600">
                Search workbook imports and website-created ingredients by food code, source code, name, source name, or alias before adding a new record.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <TextField
                label="Name, alias, or food code"
                value={duplicateQuery}
                onChange={(event) => setDuplicateQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void runDuplicateSearch(duplicateQuery);
                  }
                }}
                fullWidth
                disabled={checkingDuplicates || saving}
              />
              <Button
                onClick={() => void runDuplicateSearch(duplicateQuery)}
                disabled={checkingDuplicates || saving || duplicateQuery.trim().length < 2}
                className="h-[52px] shrink-0 gap-2 rounded-md border-0 px-4 text-[14px] font-semibold text-white shadow-none hover:brightness-95"
                style={{ backgroundColor: ACCENT }}
              >
                {checkingDuplicates ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Check duplicates
              </Button>
            </div>

            {duplicateSearchMessage ? (
              <p className="text-[13px] text-slate-600">{duplicateSearchMessage}</p>
            ) : null}

            {duplicateMatches.length > 0 ? (
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                <div className="divide-y divide-slate-200">
                  {duplicateMatches.map((match) => (
                    <div key={match.id} className="space-y-2 px-3 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-[14px] font-semibold text-slate-900">{match.name}</p>
                          <p className="text-[13px] text-slate-600">
                            {match.sourceId || 'No food code'} · {match.sourceLabel}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {match.exactMatch ? <Tag variant="danger" size="sm">Exact match</Tag> : <Tag variant="warning" size="sm">Possible match</Tag>}
                          <Tag variant={match.isActive ? 'info' : 'neutral'} size="sm">{match.isActive ? 'Active' : 'Inactive'}</Tag>
                          <Tag variant={match.isVerified ? 'info' : 'warning'} size="sm">{match.isVerified ? 'Verified' : 'Unverified'}</Tag>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-600">
                        <span>Matched: {match.matchedFields.map(formatDisplayLabel).join(', ')}</span>
                        {match.groupName ? <span>Group: {formatTaxonomyDisplayName(match.groupName)}</span> : null}
                        {match.departmentName ? <span>Department: {formatTaxonomyDisplayName(match.departmentName)}</span> : null}
                      </div>
                      {match.aliases.length > 0 ? (
                        <p className="text-[13px] text-slate-600">Aliases: {match.aliases.join(', ')}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TextField
              label="Item Type"
              value={form.itemType}
              onChange={(event) => handleItemTypeChange(event.target.value as TaxonomyItemType)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, itemTypeOptions),
              }}
            >
              {itemTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Food Code (Preview)"
              value={form.sourceId}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Source Name"
              value={form.sourceName}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Source Code"
              value={form.sourceCode}
              onChange={(event) => updateForm('sourceCode', event.target.value)}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Source Food Name"
              value={form.sourceFoodName}
              onChange={(event) => updateForm('sourceFoodName', event.target.value)}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Food Name"
              value={form.name}
              onChange={(event) => updateForm('name', event.target.value)}
              fullWidth
              required
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Group"
              value={form.groupId}
              onChange={(event) => handleGroupChange(event.target.value)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, groupOptions),
              }}
            >
              {groupOptions.map((group) => (
                <MenuItem key={group.value} value={group.value}>
                  {group.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Department"
              value={form.departmentId}
              onChange={(event) => updateForm('departmentId', event.target.value)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving || !form.groupId}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, departmentOptions),
              }}
            >
              {departmentOptions.map((department) => (
                <MenuItem key={department.value} value={department.value}>
                  {department.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Processing State"
              value={form.processingState}
              onChange={(event) => updateForm('processingState', event.target.value)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, processingStateOptions),
              }}
            >
              <MenuItem value="" disabled>Select processing state</MenuItem>
              {processingStateOptions.map((state) => (
                <MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Physical State"
              value={form.physicalState}
              onChange={(event) => updateForm('physicalState', event.target.value)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, physicalStateOptions),
              }}
            >
              <MenuItem value="" disabled>Select physical state</MenuItem>
              {physicalStateOptions.map((state) => (
                <MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Dietary Class"
              value={form.dietaryClass}
              onChange={(event) => updateForm('dietaryClass', event.target.value)}
              select
              fullWidth
              required
              disabled={loadingMetadata || saving}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                renderValue: (value) => formatIngredientSelectValue(value, dietaryClassOptions),
              }}
            >
              <MenuItem value="" disabled>Select dietary class</MenuItem>
              {dietaryClassOptions.map((dietaryClass) => (
                <MenuItem key={dietaryClass.value} value={dietaryClass.value}>{dietaryClass.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Aliases (comma separated)"
              value={form.aliasesInput}
              onChange={(event) => updateForm('aliasesInput', event.target.value)}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Reference Weight"
              value={form.referenceWeight}
              onChange={(event) => updateForm('referenceWeight', event.target.value)}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              required
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Reference Unit"
              value={formatDisplayLabel(form.referenceUnit)}
              onChange={(event) => updateForm('referenceUnit', event.target.value)}
              onBlur={() => {
                const normalized = form.referenceUnit.trim().toLowerCase();
                if (normalized !== form.referenceUnit) {
                  updateForm('referenceUnit', normalized);
                }
              }}
              fullWidth
              required
              disabled={loadingMetadata || saving}
            />

            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.isBranded}
                  onChange={(event) => updateForm('isBranded', event.target.checked)}
                  disabled={loadingMetadata || saving}
                />
              )}
              label="Branded Product"
            />

            <TextField
              label="Yield Factor"
              value={form.yieldFactor}
              onChange={(event) => updateForm('yieldFactor', event.target.value)}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Retention Factor"
              value={form.retentionFactor}
              onChange={(event) => updateForm('retentionFactor', event.target.value)}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Glycemic Index"
              value={form.glycemicIndex}
              onChange={(event) => updateForm('glycemicIndex', event.target.value)}
              type="number"
              inputProps={{ min: 0, max: 100, step: 1 }}
              fullWidth
              disabled={loadingMetadata || saving}
            />

            <TextField
              label="Glycemic Load"
              value={calculatedGlycemicLoad !== null ? formatDecimal(calculatedGlycemicLoad) : ''}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              helperText={glycemicLoadHelperText}
              InputProps={{ readOnly: true }}
              disabled={loadingMetadata || saving}
            />
          </div>

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => updateForm('description', event.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={loadingMetadata || saving}
          />

          <div className="space-y-6 rounded-lg border border-slate-200 p-4">
            <div>
              <h3 className="text-[16px] font-semibold text-slate-900">Nutrition Per 100g</h3>
              <p className="text-[14px] text-slate-500">
                Macros and micros are listed separately below. Enter what you know and leave the rest empty.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="text-[15px] font-semibold text-slate-700">Macros</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {MACRO_NUTRITION_FIELDS.map((field) => (
                  <TextField
                    key={field.key}
                    label={`${field.label} (${field.unit})`}
                    value={form.nutritionValues[field.key] || ''}
                    onChange={(event) => updateNutritionField(field.key, event.target.value)}
                    type="number"
                    inputProps={{ min: 0, step: 'any' }}
                    fullWidth
                    disabled={loadingMetadata || saving}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-[15px] font-semibold text-slate-700">Micros</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {MICRO_NUTRITION_FIELDS.map((field) => (
                  <TextField
                    key={field.key}
                    label={`${field.label} (${field.unit})`}
                    value={form.nutritionValues[field.key] || ''}
                    onChange={(event) => updateNutritionField(field.key, event.target.value)}
                    type="number"
                    inputProps={{ min: 0, step: 'any' }}
                    fullWidth
                    disabled={loadingMetadata || saving}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-[16px] font-semibold text-slate-900">Serving Conversions</h3>
                <p className="text-[14px] text-slate-500">Add optional units like cup, tbsp, or piece with gram values.</p>
              </div>
              <Button
                onClick={addServingOverride}
                disabled={loadingMetadata || saving}
                className="h-8 gap-1 rounded-md border border-slate-300 bg-white px-2 text-[14px] font-semibold text-slate-700 shadow-none hover:bg-slate-50"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Serving
              </Button>
            </div>

            {form.servingOverrides.length === 0 ? (
              <p className="text-[14px] text-slate-500">No extra serving conversions added yet.</p>
            ) : (
              <div className="space-y-3">
                {form.servingOverrides.map((row, index) => (
                  <div key={row.id} className="grid grid-cols-1 gap-2 sm:grid-cols-[1.1fr_0.8fr_0.8fr_0.8fr_auto_auto]">
                    <TextField
                      label={`Serving Unit #${index + 1}`}
                      value={row.servingId}
                      onChange={(event) => updateServingOverride(row.id, 'servingId', event.target.value)}
                      fullWidth
                      disabled={loadingMetadata || saving}
                      placeholder="e.g. cup"
                    />
                    <TextField
                      label="Serving Size"
                      value={row.servingSize}
                      onChange={(event) => updateServingOverride(row.id, 'servingSize', event.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 'any' }}
                      fullWidth
                      disabled={loadingMetadata || saving}
                    />
                    <TextField
                      label="Grams"
                      value={row.grams}
                      onChange={(event) => updateServingOverride(row.id, 'grams', event.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 'any' }}
                      fullWidth
                      disabled={loadingMetadata || saving}
                    />
                    <TextField
                      label="ML"
                      value={row.ml}
                      onChange={(event) => updateServingOverride(row.id, 'ml', event.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 'any' }}
                      fullWidth
                      disabled={loadingMetadata || saving}
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={row.isDefault}
                          onChange={(event) => updateServingOverrideBoolean(row.id, 'isDefault', event.target.checked)}
                          disabled={loadingMetadata || saving}
                        />
                      )}
                      label="Default"
                    />
                    <Button
                      onClick={() => removeServingOverride(row.id)}
                      disabled={loadingMetadata || saving}
                      className="h-10 gap-1 rounded-md border border-slate-300 bg-white px-3 text-[14px] font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <TextField
              label="Default Serving Unit (Optional)"
              value={form.defaultServingId}
              onChange={(event) => updateForm('defaultServingId', event.target.value)}
              select
              fullWidth
              disabled={loadingMetadata || saving}
              helperText={`If not set, reference unit (${formatDisplayLabel(form.referenceUnit.trim().toLowerCase() || 'gram')}) is used.`}
              {...INGREDIENT_SHRINK_LABEL_PROPS}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                displayEmpty: true,
                renderValue: (value) => {
                  const normalized = String(value ?? '').trim().toLowerCase();
                  if (!normalized) return '';
                  return formatDisplayLabel(normalized);
                },
              }}
            >
              <MenuItem value="">
                Use reference unit ({formatDisplayLabel(form.referenceUnit.trim().toLowerCase() || 'gram')})
              </MenuItem>
              {servingUnitOptions.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {formatDisplayLabel(unit)}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <TextField
              label="Dietary Tags"
              value={form.dietaryTagIds}
              onChange={(event) => updateTagSelection('dietaryTagIds', event.target.value)}
              select
              fullWidth
              disabled={loadingMetadata || saving}
              {...INGREDIENT_SHRINK_LABEL_PROPS}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => renderSelectedTags(selected, dietaryTagMap),
              }}
            >
              {(metadata?.tags.dietary || []).map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {formatDisplayLabel(tag.displayName)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Allergen Tags"
              value={form.allergenTagIds}
              onChange={(event) => updateTagSelection('allergenTagIds', event.target.value)}
              select
              fullWidth
              disabled={loadingMetadata || saving}
              {...INGREDIENT_SHRINK_LABEL_PROPS}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => renderSelectedTags(selected, allergenTagMap),
              }}
            >
              {(metadata?.tags.allergen || []).map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {formatDisplayLabel(tag.displayName)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Intolerance Tags"
              value={form.intoleranceTagIds}
              onChange={(event) => updateTagSelection('intoleranceTagIds', event.target.value)}
              select
              fullWidth
              disabled={loadingMetadata || saving}
              {...INGREDIENT_SHRINK_LABEL_PROPS}
              SelectProps={{
                ...INGREDIENT_SELECT_MENU_PROPS,
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => renderSelectedTags(selected, intoleranceTagMap),
              }}
            >
              {(metadata?.tags.intolerance || []).map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {formatDisplayLabel(tag.displayName)}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="flex flex-wrap gap-2">
            <Tag variant="neutral" size="sm">{`Type: ${ITEM_TYPE_OPTIONS.find((item) => item.value === form.itemType)?.label || form.itemType}`}</Tag>
            {form.groupId ? <Tag variant="neutral" size="sm">{`Group: ${formatTaxonomyDisplayName(selectedGroup?.name || form.groupId)}`}</Tag> : null}
            {form.departmentId ? (
              <Tag variant="neutral" size="sm">
                {`Department: ${formatTaxonomyDisplayName(selectedDepartmentOptions.find((item) => item.id === form.departmentId)?.name || form.departmentId)}`}
              </Tag>
            ) : null}
            <Tag variant="info" size="sm">{`Dietary Tags: ${form.dietaryTagIds.length}`}</Tag>
            <Tag variant="danger" size="sm">{`Allergens: ${form.allergenTagIds.length}`}</Tag>
            <Tag variant="warning" size="sm">{`Intolerances: ${form.intoleranceTagIds.length}`}</Tag>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => void handleSubmit()}
              disabled={loadingMetadata || saving}
              className="h-9 gap-2 rounded-md border-0 px-4 text-[14px] font-semibold text-white shadow-none hover:brightness-95"
              style={{ backgroundColor: ACCENT }}
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Create Ingredient'}
            </Button>
          </div>
        </Box>
      </Paper>
    </Box>
  );
};

export default IngredientsPage;
